import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { User } from 'src/app/its/models/user';
import { AuthorityService } from './authority.service';
import { EnteUO } from '../models/EnteUO';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private userSubject: BehaviorSubject<User | null>;
    public user: Observable<User | null>;

    constructor(
        private router: Router,
        private http: HttpClient,
        private authorityService: AuthorityService
    ) {
        this.userSubject = new BehaviorSubject<User | null>(null);
        this.user = this.userSubject.asObservable();
    }

    public get getUserValue() {
        return this.userSubject.value;
    }

    login(username: string, password: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/login`, { username, password }, { withCredentials: true })
            .pipe(map(user => {
                if (user.hasOwnProperty("is2StepVerificationRequired") && user.is2StepVerificationRequired == true) {
                    return user
                }
                else {
                    this.userSubject.next(user);
                    this.startRefreshTokenTimer();
                    // store user details and jwt token in local storage to keep user logged in between page refreshes
                    localStorage.setItem('currentUser', JSON.stringify(user));
                    localStorage.removeItem('currentUO');
                    localStorage.removeItem("idEnte");  // rimuovo forzatamente l'idEnte in modo da obbligare l'utente a riselezionarlo al login
                    return user;
                }
            }));
    }

    login2fa(username: string, provider: string, otp: string, token: string) {
        const headers = { 'Authorization': `Bearer ${token}` }
        return this.http.post<any>(`${environment.apiUrl}/authenticate/login2fa`, { username, provider, otp }, { headers })
            .pipe(map(user => {
                this.userSubject.next(user);
                this.startRefreshTokenTimer();
                //localStorage.setItem("refreshToken",res.refreshToken)				
                localStorage.setItem('currentUser', JSON.stringify(user));
                localStorage.removeItem('currentUO');
                localStorage.removeItem("idEnte");  // rimuovo forzatamente l'idEnte in modo da obbligare l'utente a riselezionarlo al login
                return user;
            }));
    }

    logout() {
        this.http.post<any>(`${environment.apiUrl}/authenticate/revoke-token`, {}, { withCredentials: true }).subscribe(
            (response) => {
                // Gestire la risposta di successo
                console.log('Logout:', response.message);
                // Puoi eseguire ulteriori azioni a seguito della risposta qui
            },
            (error) => {
                // Gestire gli errori della richiesta
                console.error('Errore nella richiesta:', error);
                // Puoi eseguire azioni specifiche a seguito di un errore qui
            }
        );

        this.stopRefreshTokenTimer();
        this.userSubject.next(null);

        // remove some variables from local storage to log user out
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentUO');
        localStorage.removeItem("idEnte");

        // cancello il BeaviorSubject di selezione EnteUO dell'utente notificatore
        // recupero i dati Ente / UO
        this.authorityService.setEnteUOData(null, null);

        this.router.navigate(['authentication/login']);
    }

    refreshToken() {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/refresh-token`, {}, { withCredentials: true })
            .pipe(map((user) => {
                this.userSubject.next(user);
                let uo: EnteUO = JSON.parse(localStorage.getItem("currentUO"));
                this.authorityService.setEnteUO(uo);
                this.startRefreshTokenTimer();
                return user;
            }));
    }

    // helper methods

    private refreshTokenTimeout?: NodeJS.Timeout;

    private startRefreshTokenTimer() {
        // parse json object from base64 encoded jwt token
        const jwtBase64 = this.getUserValue!.token!.split('.')[1];
        const jwtToken = JSON.parse(atob(jwtBase64));

        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (60 * 1000);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
    }

    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }


    /**
     * User Forgot Password
     * @param email 
     * @returns 
     */
    forgotPassword(email: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/forgot-password`, { email });
    }

    /**
     * User register
     *
     * @param firstName
     * @param lastName
     * @param email
     * @param password
     * @param confirmPassword
     * @param acceptTerms
     * @returns user
     */

    register(firstName: string, lastName: string, username: string, email: string, taxId: string, password: string, acceptTerms: boolean) {
        return this.http.post(`${environment.apiUrl}/authenticate/register`, { firstName, lastName, username, email, taxId, password, acceptTerms });
    }


    /**
     * User Verify Email
     *
     * @param token
     
     * @returns user
     */
    verifyEmail(token: string, email: string) {
        return this.http.post(`${environment.apiUrl}/authenticate/verify-email`, { token, email });
    }


    /**
      * Reset Password
      *
      * @param token
      * @param password
      * @param confirmPassword
      * @returns user
      */
    resetPassword(email: string, token: string, password: string, confirmPassword: string) {
        return this.http.post(`${environment.apiUrl}/authenticate/reset-password`, { email, token, password, confirmPassword });
    }

    revokeToken(email: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/revoketoken`, email)
    }


    sendContactMessage(email: string, name: string, message: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/send-contact-message`, { email, name, message });
    }


    deleteProfile(email: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/deleteprofile`, { email });
    }

    //Ritorna un oggetto User ridotto alcune informazioni della tabella User ed i Ruoli 
    getById(id: string) {
        return this.http.get<any>(`${environment.apiUrl}/authenticate/${id}`);
    }

    changepwd(email: string, currentPassword: string, newPassword: string) {
        return this.http.post<any>(`${environment.apiUrl}/authenticate/changepassword`, { email, currentPassword, newPassword });
    }

}